import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getEnrolledCourses } from '../../api/educentialApi';
import { BookOpen, Target, CheckCircle, Clock, Calendar } from 'lucide-react';
import './MyCourses.css';

function MyCourses() {
  const navigate = useNavigate();
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, in-progress, completed

  useEffect(() => {
    loadEnrolledCourses();
  }, []);

  const loadEnrolledCourses = async () => {
    try {
      setLoading(true);
      const response = await getEnrolledCourses();
      if (response.success) {
        setEnrollments(response.data);
      }
    } catch (err) {
      setError(err.message || 'Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const filteredEnrollments = enrollments.filter((enrollment) => {
    if (filter === 'in-progress') return enrollment.status === 'ACTIVE' && enrollment.progress < 100;
    if (filter === 'completed') return enrollment.status === 'COMPLETED' || enrollment.progress === 100;
    return true;
  });

  const calculateStats = () => {
    const total = enrollments.length;
    const completed = enrollments.filter((e) => e.status === 'COMPLETED' || e.progress === 100).length;
    const inProgress = enrollments.filter((e) => e.status === 'ACTIVE' && e.progress < 100).length;

    const totalMinutes = enrollments.reduce((sum, e) => {
      const course = e.Course;
      const estimatedMinutes = (course.duration || 0) * 60;
      const completedMinutes = (estimatedMinutes * e.progress) / 100;
      return sum + completedMinutes;
    }, 0);

    const hoursLearned = Math.round(totalMinutes / 60);

    return { total, completed, inProgress, hoursLearned };
  };

  const stats = calculateStats();

  if (loading) {
    return (
      <div className="my-courses-container">
        <div className="loading">Loading your courses...</div>
      </div>
    );
  }

  return (
    <div className="my-courses-container">
      <div className="my-courses-header">
        <div>
          <h1>My Learning</h1>
          <p>Track your progress and continue learning</p>
        </div>
        <button className="btn-browse" onClick={() => navigate('/courses/catalog')}>
          Browse Courses
        </button>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">
            <BookOpen className="w-6 h-6" />
          </div>
          <div>
            <h3>{stats.total}</h3>
            <p>Courses Enrolled</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <Target className="w-6 h-6" />
          </div>
          <div>
            <h3>{stats.inProgress}</h3>
            <p>In Progress</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <CheckCircle className="w-6 h-6" />
          </div>
          <div>
            <h3>{stats.completed}</h3>
            <p>Completed</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <Clock className="w-6 h-6" />
          </div>
          <div>
            <h3>{stats.hoursLearned}h</h3>
            <p>Hours Learned</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filter-tabs">
        <button
          className={filter === 'all' ? 'active' : ''}
          onClick={() => setFilter('all')}
        >
          All Courses ({enrollments.length})
        </button>
        <button
          className={filter === 'in-progress' ? 'active' : ''}
          onClick={() => setFilter('in-progress')}
        >
          In Progress ({stats.inProgress})
        </button>
        <button
          className={filter === 'completed' ? 'active' : ''}
          onClick={() => setFilter('completed')}
        >
          Completed ({stats.completed})
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Courses List */}
      {filteredEnrollments.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">
            <BookOpen className="w-16 h-16 text-gray-400" />
          </div>
          <h3>
            {filter === 'all'
              ? 'No enrolled courses yet'
              : filter === 'completed'
              ? 'No completed courses'
              : 'No courses in progress'}
          </h3>
          <p>
            {filter === 'all'
              ? 'Start learning by browsing our course catalog'
              : 'Keep learning to complete your courses!'}
          </p>
          {filter === 'all' && (
            <button className="btn-primary" onClick={() => navigate('/courses/catalog')}>
              Browse Courses
            </button>
          )}
        </div>
      ) : (
        <div className="courses-list">
          {filteredEnrollments.map((enrollment) => (
            <EnrollmentCard key={enrollment.id} enrollment={enrollment} />
          ))}
        </div>
      )}
    </div>
  );
}

function EnrollmentCard({ enrollment }) {
  const navigate = useNavigate();
  const course = enrollment.Course;

  const totalLessons = course.Modules?.reduce(
    (sum, module) => sum + (module.Lessons?.length || 0),
    0
  ) || 0;

  const completedLessons = Math.round((totalLessons * enrollment.progress) / 100);

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div
      className="enrollment-card"
      onClick={() => navigate(`/courses/${course.id}/learn`)}
    >
      <div className="enrollment-thumbnail">
        {course.thumbnail ? (
          <img src={course.thumbnail} alt={course.title} />
        ) : (
          <div className="placeholder-thumbnail">{course.category.charAt(0)}</div>
        )}
        {enrollment.progress === 100 && (
          <div className="completion-badge flex items-center space-x-1">
            <CheckCircle className="w-4 h-4" />
            <span>Completed</span>
          </div>
        )}
      </div>

      <div className="enrollment-body">
        <div className="enrollment-header">
          <div>
            <h3>{course.title}</h3>
            <p className="course-meta">
              {course.level} • {course.category}
              {course.term && ` • ${course.term}`}
            </p>
          </div>
        </div>

        <div className="progress-section">
          <div className="progress-info">
            <span className="progress-label">
              {completedLessons} of {totalLessons} lessons
            </span>
            <span className="progress-percentage">{enrollment.progress}%</span>
          </div>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${enrollment.progress}%` }}
            />
          </div>
        </div>

        <div className="enrollment-footer">
          <div className="enrollment-info">
            <span className="info-item">
              <Calendar className="w-4 h-4" />
              Enrolled {formatDate(enrollment.enrolledAt)}
            </span>
            {enrollment.lastAccessedAt && (
              <span className="info-item">
                <Clock className="w-4 h-4" />
                Last accessed {formatDate(enrollment.lastAccessedAt)}
              </span>
            )}
          </div>
          <button className="btn-continue">
            {enrollment.progress === 0 ? 'Start Learning' : 'Continue Learning'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default MyCourses;
